import { DOOR_REFUSAL_REASONS, MAX_DOOR_PROMPT_NAME_CHARS } from "../zones/doors.ts";
import { HANDOFF_CAUSES, MAX_HANDOFF_MAP_CHARS, MAX_HANDOFF_NAME_CHARS, MAX_RESPAWN_POINT_MAP_CHARS } from "./handoff.ts";
import { CURRENCY_CODE_PATTERN, CURRENCY_MAX, MAX_WALLET_CURRENCIES } from "../currency/coins.ts";
import { MAX_CHANNEL_WORD_CHARS, MAX_MUTE_MINUTES } from "../chat/channels.ts";
import { MAX_LOOKUP_PREFIX_CHARS, MAX_NAME_MATCHES, MIN_LOOKUP_CHARS } from "../chat/lookup.ts";
import { MAX_RELAYED_TEXT_UNITS } from "../chat/text.ts";
import { EMOTE_SET_PATTERN, MAX_EMOTE_SETS, MAX_EMOTE_SET_CHARS } from "../emotes/sets.ts";
import { MAX_EMOTE_ID } from "../emotes/table.ts";
import { PARTY_INVITE_MS, PARTY_MAP_CHARS, PARTY_SIZE } from "../party/constants.ts";
import { PARTY_NOTICE_KINDS, PARTY_REFUSALS } from "../party/words.ts";
import { BAG_SORT_ORDERS } from "../items/bag.ts";
import { MAX_LOOT_ENTRIES } from "../items/loot.ts";
import { BACKGROUND_IDS, BAR_SLOTS, BLOWS, TOOL_BELT_SLOTS, ITEM_ID_PATTERN, MAX_ITEM_ID_CHARS, MAX_ITEM_STACK, RULES_ID_PATTERN } from "../rules/types.ts";
import { BADGE_MAX, STAFF_TYPES } from "../gm/badge.ts";
import { FEEDBACK_ACCESS_STATES } from "../gm/feedback.ts";
import { ACKNOWLEDGED_ACTIONS, NOTICE_SENDERS, STAFF_NOTICE_KINDS } from "../gm/moderation.ts";
import { Away } from "./away.ts";
import { DEBUG_RANGE_START, MessageType, isAllowed, isNotReady, messageInfo, type MessageDirection } from "./catalogue.ts";
import { CloseCode, MAX_BLOCKED_FAMILIES, MAX_CHAT_FRAME_CHARS, MAX_ERROR_MESSAGE_CHARS, MAX_INTERACTION_TEXT_CHARS, MAX_U16, MAX_U32, MAX_VERB_CHARS, MAX_WIRE_BAG_SLOTS, MAX_ZONE_TOKEN_CHARS, NO_HANDLE, PROTOCOL_VERSION } from "./limits.ts";
import { MAX_NAME_PART_BYTES } from "./names.ts";
import { SIXTEENTHS } from "../movement/step.ts";
import { MAX_EDGE_NEAR_VERSION_CHARS } from "./edge-near.ts";
import { MAX_MAP_ART_APPEARANCE, MAX_MAP_PLACES, MAX_MAP_PLACE_NAME_CHARS, MAX_MAP_PLACE_TILES } from "./map-places.ts";
import { MAX_PERF_INTERVAL_MS, MIN_PERF_INTERVAL_MS } from "./perf.ts";
import { ATTRIBUTES } from "../rules/attributes.ts";
import { ATTRIBUTE_REFUSALS, MAX_SPEND_POINTS } from "../rules/attribute-spend.ts";
import { SKY_OVERRIDES } from "../light/sky.ts";
import { COMPANION_LINE_PATTERN, COMPANION_NAMING_STATES, MAX_COMPANION_LINE_CHARS } from "./companion-text.ts";
import { FIRST_NAME_PATTERN, MAX_FIRST_NAME_CHARS, MIN_FIRST_NAME_CHARS } from "../names/character-name.ts";
import { MAX_SOURCE_KEY_CHARS, SOURCE_KEY_PATTERN } from "./source-text.ts";
import { HARVEST_REASON_PATTERN, HARVEST_STATES, MAX_HARVEST_REASON_CHARS } from "./harvest-text.ts";
import { MAX_TRAVEL_FOCUS, MAX_TRAVEL_NAME_CHARS, MAX_TRAVEL_PLACES, MAX_TRAVEL_REASON_CHARS, TRAVEL_OFFER_SOURCES, TRAVEL_PLACE_KINDS, TRAVEL_PLACE_REASON_PATTERN, TRAVEL_REASON_PATTERN, TRAVEL_STATES } from "./travel.ts";
import { MAX_DIALOGUE_CHOICES, NODE_ID_PATTERN } from "../content/dialogue.ts";
import { CONTENT_FILE_ID_PATTERN, LINE_ID_PATTERN } from "../content/line.ts";
import { DIALOGUE_CLOSE_REASONS, MAX_DIALOGUE_TEXT_UNITS } from "./dialogue.ts";
import { DIALOGUE_MARKS } from "../npcs/dialogue-marks.ts";
import { MAX_STANDING_TIERS } from "../npcs/standing.ts";
import { MAX_RESPAWN_POINTS, MAX_RESPAWN_POINT_KIND_CHARS, MAX_RESPAWN_POINT_NAME_CHARS, RESPAWN_POINT_KIND_PATTERN } from "./respawn.ts";

/**
 * Text frames (PROTOCOL.md section 6): each message is a field table that one validator applies, so the
 * table is the schema. Parsing never throws; a fault closes with a code (ADR-030), and a well-formed frame of a
 * type the catalogue does not know is told apart from a fault, so a page can ignore a later server's message
 * (PROTOCOL.md 2) while a server still closes on one.
 */

/**
 * One field of a text message: a whole number within bounds, a number of tiles that is a whole number of sixteenths
 * from 0 to a bound (`sixteenths`), a string within bounds and an optional pattern, one of a closed set of words
 * (`enum`), true or false, null, an object of exactly its fields, a list of one schema (its items distinct when
 * `distinct`, which compares them as JSON, or, with `distinctAt`, no two of its items alike at that position, for a
 * list of tuples keyed by one of their positions; a list of numbers each greater than the one before it when
 * `ascending`; a list of objects each after the one before it when compared field by field in the order `orderedBy`
 * names, so none repeats), a fixed list of positions each with its own schema (`tuple`), or any one of several schemas
 * (`oneOf`).
 */
type FieldSchema =
  | { readonly kind: "integer"; readonly min: number; readonly max: number }
  | { readonly kind: "sixteenths"; readonly max: number }
  | { readonly kind: "string"; readonly minChars: number; readonly maxChars: number; readonly pattern?: RegExp }
  | { readonly kind: "enum"; readonly values: readonly string[] }
  | { readonly kind: "boolean" }
  | { readonly kind: "null" }
  | { readonly kind: "object"; readonly fields: Readonly<Record<string, FieldSchema>> }
  | { readonly kind: "array"; readonly of: FieldSchema; readonly maxItems: number; readonly minItems?: number; readonly distinct?: boolean; readonly distinctAt?: number; readonly ascending?: boolean; readonly orderedBy?: readonly string[] }
  | { readonly kind: "tuple"; readonly items: readonly FieldSchema[] }
  | { readonly kind: "oneOf"; readonly of: readonly FieldSchema[] };

/**
 * A verb name: lower-case letters and underscores.
 */
export const VERB_PATTERN = /^[a-z_]+$/;

const VERB: FieldSchema = { kind: "string", minChars: 1, maxChars: MAX_VERB_CHARS, pattern: VERB_PATTERN };

/**
 * A chat refusal's reason: a lower-case word from the closed set ADR-068 decision 3 lists, which the page words.
 */
export const CHAT_REASON_PATTERN = /^[a-z]+$/;

/**
 * A rules version as `welcome` carries it (ADR-069 decision 8): lower-case letters, digits and hyphens, so a later
 * version's shape is a new value rather than a malformed frame.
 */
export const RULES_VERSION_PATTERN = /^[a-z0-9-]+$/;

/**
 * A reading layout as `readingLayout` names it (ADR-077 decision 6): 1 to 16 lower-case letters, so a layout a later
 * server names is a value a page reads as scroll rather than a malformed frame.
 */
export const READING_LAYOUT_PATTERN = /^[a-z]+$/;

/**
 * The longest reading layout name the wire carries.
 */
const MAX_READING_LAYOUT_CHARS = 16;

/**
 * `maintenance`'s `endsInMs` when the schedule names no end (ADR-078 decision 6).
 */
export const NO_MAINTENANCE_END = -1;

/**
 * `queue`'s `etaMs` when the gateway has too few admissions to estimate the wait (ADR-127 decision 7).
 */
export const NO_QUEUE_ESTIMATE = -1;

/**
 * `moderation`'s `endsInMs` for an action with no end, a permanent ban (ADR-056's amendment of 2026-10-04, item 9).
 */
export const NO_MODERATION_END = -1;

/**
 * A moderation word as the wire carries it, an action or a notice's kind: 1 to 16 lower-case letters, so a word a
 * later server names is a value the page ignores rather than a malformed frame.
 */
export const MODERATION_WORD_PATTERN = /^[a-z]+$/;

/**
 * The longest moderation word, and the most actions `moderationOffer` lists.
 */
const MAX_MODERATION_WORD_CHARS = 16;

const MODERATION_WORD: FieldSchema = { kind: "string", minChars: 1, maxChars: MAX_MODERATION_WORD_CHARS, pattern: MODERATION_WORD_PATTERN };

const FIRST_NAME: FieldSchema = { kind: "string", minChars: 1, maxChars: MAX_NAME_PART_BYTES };
/**
 * A notice's id as `staffNotice` and `staffNoticeAck` carry it: the moderation row's uuid, lower-case and hyphenated.
 */
export const NOTICE_ID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;

const NOTICE_ID: FieldSchema = { kind: "string", minChars: 36, maxChars: 36, pattern: NOTICE_ID_PATTERN };

const FAMILY_NAME: FieldSchema = { kind: "string", minChars: 0, maxChars: MAX_NAME_PART_BYTES };
const NAMED_FAMILY: FieldSchema = { kind: "string", minChars: 1, maxChars: MAX_NAME_PART_BYTES };
const CHAT_REQUEST_TEXT: FieldSchema = { kind: "string", minChars: 1, maxChars: MAX_CHAT_FRAME_CHARS };
const RELAYED_TEXT: FieldSchema = { kind: "string", minChars: 1, maxChars: MAX_RELAYED_TEXT_UNITS };
const LOOKUP_PREFIX: FieldSchema = { kind: "string", minChars: MIN_LOOKUP_CHARS, maxChars: MAX_LOOKUP_PREFIX_CHARS };
const MATCHED_NAME: FieldSchema = { kind: "object", fields: { firstName: FIRST_NAME, familyName: NAMED_FAMILY } };
/**
 * A channel's word as the wire carries it (ADR-068's amendment "Global, Trade and Help", item 6): lower-case
 * letters, so a channel a later server opens is a value the page ignores and the gateway refuses, never a
 * malformed frame.
 */
export const CHANNEL_WORD_PATTERN = /^[a-z]+$/;

/**
 * The most channels and mutes `channels` lists: the three channels and `all`, with room for channels opened later.
 */
const MAX_LISTED_CHANNELS = 8;

const CHANNEL_WORD: FieldSchema = { kind: "string", minChars: 1, maxChars: MAX_CHANNEL_WORD_CHARS, pattern: CHANNEL_WORD_PATTERN };
const MUTE_MINUTES: FieldSchema = { kind: "integer", min: 0, max: MAX_MUTE_MINUTES };
const HELD_MUTE: FieldSchema = { kind: "object", fields: { channel: CHANNEL_WORD, waitMs: { kind: "integer", min: 1, max: MAX_U32 } } };
const RESPAWN_POINT_FIELDS: FieldTable = {
  kind: { kind: "string", minChars: 1, maxChars: MAX_RESPAWN_POINT_KIND_CHARS, pattern: RESPAWN_POINT_KIND_PATTERN },
  name: { kind: "string", minChars: 0, maxChars: MAX_RESPAWN_POINT_NAME_CHARS },
};
/**
 * A respawn point: its kind and name, and from 6.91.0 the display name of the map it stands on (ADR-106 decision 19),
 * which a point on the map the offer is sent on leaves out or sends empty, so an offer of this map's points alone is
 * the offer a page before 6.91.0 reads.
 */
const RESPAWN_POINT: FieldSchema = {
  kind: "oneOf",
  of: [
    { kind: "object", fields: RESPAWN_POINT_FIELDS },
    { kind: "object", fields: { ...RESPAWN_POINT_FIELDS, map: { kind: "string", minChars: 0, maxChars: MAX_RESPAWN_POINT_MAP_CHARS } } },
  ],
};

/**
 * Why a `useItem`, a `bagMove`, a `bagSplit`, a `destroyItem`, a `bagSort`, a `bagKeep` or a `dropItem` was refused (ADR-091 decision 10, in its order of checks,
 * `unavailable` for an operation the store refused, and `ground` for a drop the ground has no room for, 6.58.0, P2.8-T34); `itemRefused` carries one, and a reason outside the set is a malformed frame.
 */
export const ITEM_REFUSAL_REASONS = ["dead", "stunned", "busy", "duel", "empty", "kind", "cooldown", "healthy", "unavailable", "ground"] as const;
export type ItemRefusalReason = (typeof ITEM_REFUSAL_REASONS)[number];

/**
 * Why a line of a `lootTake` was refused (ADR-091 decision 20); `lootTaken` carries one per refused line.
 */
export const LOOT_REFUSAL_REASONS = ["full", "taken", "closed", "reach", "busy", "unavailable"] as const;
export type LootRefusalReason = (typeof LOOT_REFUSAL_REASONS)[number];

/**
 * Why a `pickUp` is refused (P2.8-T64, ADR-091's amendment for P2.8-T13, decision 3), the checks' order: the character
 * is dead; the handle is no ground item in its view, or one a pick-up holds, or one gone; it is out of reach; it is
 * another character's while owned; its whole line does not fit the bag; the character's queue is full; the store
 * refused the pick-up. A refusal carries `waitMs`, for `owned` the time until the item turns public (6.56.0, P2.8-T74),
 * 0 for every other reason.
 */
export const PICK_UP_REFUSAL_REASONS = ["dead", "taken", "reach", "owned", "full", "busy", "unavailable"] as const;
export type PickUpRefusalReason = (typeof PICK_UP_REFUSAL_REASONS)[number];

/**
 * The requests an `itemRefused` answers: a use, a move, a split and a destroy (P2.8-T14), a sort (ADR-125 decision 3.4),
 * a keep (decision 6.4) and a drop (6.58.0, P2.8-T34).
 */
export const ITEM_REQUESTS = ["use", "move", "split", "destroy", "sort", "keep", "drop"] as const;
export type ItemRequest = (typeof ITEM_REQUESTS)[number];

/**
 * What changed a bag, as `bagChanged` names it, a drop among them (6.58.0, P2.8-T34). A keep changes no item or count, so it is not among them: the page
 * learns of it from `bagKept` alone (ADR-125 decision 6.4).
 */
export const BAG_CHANGE_CAUSES = ["use", "loot", "move", "split", "destroy", "sort", "drop"] as const;
export type BagChangeCause = (typeof BAG_CHANGE_CAUSES)[number];

const BAG_SLOT: FieldSchema = { kind: "integer", min: 0, max: MAX_WIRE_BAG_SLOTS - 1 };
const ITEM_ID: FieldSchema = { kind: "string", minChars: 1, maxChars: MAX_ITEM_ID_CHARS, pattern: ITEM_ID_PATTERN };
const QUANTITY: FieldSchema = { kind: "integer", min: 1, max: MAX_ITEM_STACK };
/**
 * A chest's line: its entry's place in the loot table, so a chest holds at most `MAX_LOOT_ENTRIES` lines for a
 * character and a take names at most that many (ADR-091 decision 18).
 */
const CHEST_LINE: FieldSchema = { kind: "integer", min: 0, max: MAX_LOOT_ENTRIES - 1 };
const HELD_SLOT: FieldSchema = { kind: "tuple", items: [BAG_SLOT, ITEM_ID, QUANTITY] };

/**
 * One slot of the player's bar (ADR-121 decisions 1.1 and 5.1): empty, a blow of the held weapon, an ability by its
 * rules id, or an item by its item id; the same shape `parseBarRecord` reads.
 */
const BAR_SLOT: FieldSchema = {
  kind: "oneOf",
  of: [
    { kind: "null" },
    { kind: "tuple", items: [{ kind: "enum", values: ["blow"] }, { kind: "enum", values: BLOWS }] },
    { kind: "tuple", items: [{ kind: "enum", values: ["ability"] }, { kind: "string", minChars: 1, maxChars: 32, pattern: RULES_ID_PATTERN }] },
    { kind: "tuple", items: [{ kind: "enum", values: ["item"] }, ITEM_ID] },
  ],
};

/**
 * The player's bar on the wire: its `BAR_SLOTS` slots, slot 1 first, or null for the default bar.
 */
const BAR_SLOTS_FIELD: FieldSchema = { kind: "oneOf", of: [{ kind: "null" }, { kind: "array", of: BAR_SLOT, minItems: BAR_SLOTS, maxItems: BAR_SLOTS }] };

/**
 * The player's tool belt on the wire (ADR-142 decision 3b): its `TOOL_BELT_SLOTS` slots, each empty or an item id, and the
 * selected slot from 1, or 0 for none.
 */
const TOOL_BELT_SLOTS_FIELD: FieldSchema = { kind: "array", of: { kind: "oneOf", of: [{ kind: "null" }, ITEM_ID] }, minItems: TOOL_BELT_SLOTS, maxItems: TOOL_BELT_SLOTS };
const TOOL_BELT_SELECTED: FieldSchema = { kind: "integer", min: 0, max: TOOL_BELT_SLOTS };
const EMPTIED_SLOT: FieldSchema = { kind: "tuple", items: [BAG_SLOT, { kind: "null" }, { kind: "integer", min: 0, max: 0 }] };

const U16: FieldSchema = { kind: "integer", min: 0, max: MAX_U16 };
const NPC_HANDLE: FieldSchema = { kind: "integer", min: 0, max: NO_HANDLE - 1 };
const DIALOGUE_NODE: FieldSchema = { kind: "string", minChars: 1, maxChars: 48, pattern: NODE_ID_PATTERN };
const DIALOGUE_LINE_ID: FieldSchema = { kind: "string", minChars: 2, maxChars: 7, pattern: LINE_ID_PATTERN };
/**
 * The longest tier word on the wire in UTF-16 units: a tier's name is a plain line the size of an NPC's name, 32 code points.
 */
const MAX_TIER_NAME_UNITS = 64;
const DIALOGUE_TEXT: FieldSchema = { kind: "string", minChars: 0, maxChars: MAX_DIALOGUE_TEXT_UNITS };
/**
 * A dialogue choice (ADR-136 decision 5.3 and the amendment of 2026-10-28, decision 7): its line id and text, then its
 * marks as numbers of `DIALOGUE_MARKS` in ascending order, a check's chance as a whole percent, the background a
 * background choice is open to and a locked choice's need, each null when it does not apply. A server before the marks
 * sends the line and text alone, which a page still reads as a choice with no marks.
 */
const DIALOGUE_CHOICE: FieldSchema = {
  kind: "oneOf",
  of: [
    {
      kind: "object",
      fields: {
        line: DIALOGUE_LINE_ID,
        text: DIALOGUE_TEXT,
        marks: { kind: "array", of: { kind: "integer", min: 0, max: DIALOGUE_MARKS.length - 1 }, maxItems: DIALOGUE_MARKS.length, ascending: true },
        chance: { kind: "oneOf", of: [{ kind: "null" }, { kind: "integer", min: 0, max: 100 }] },
        background: { kind: "oneOf", of: [{ kind: "null" }, { kind: "string", minChars: 1, maxChars: 32, pattern: RULES_ID_PATTERN }] },
        need: { kind: "oneOf", of: [{ kind: "null" }, DIALOGUE_TEXT] },
      },
    },
    { kind: "object", fields: { line: DIALOGUE_LINE_ID, text: DIALOGUE_TEXT } },
  ],
};
/**
 * An NPC's mood tier in the dialogue frame (ADR-146 decision 8.2 and its amendment of 2026-10-31): the tier's word, its
 * place among the template's tiers from 0, how many tiers there are, the next tier's word (null at the last) and, for a conversation that can feed the NPC,
 * whether it still wants today's feed. Never the standing itself.
 */
const DIALOGUE_TIER: FieldSchema = {
  kind: "oneOf",
  of: [
    { kind: "null" },
    {
      kind: "object",
      fields: {
        name: { kind: "string", minChars: 1, maxChars: MAX_TIER_NAME_UNITS },
        at: { kind: "integer", min: 0, max: MAX_STANDING_TIERS - 1 },
        of: { kind: "integer", min: 1, max: MAX_STANDING_TIERS },
        next: { kind: "oneOf", of: [{ kind: "null" }, { kind: "string", minChars: 1, maxChars: MAX_TIER_NAME_UNITS }] },
        hungry: { kind: "oneOf", of: [{ kind: "null" }, { kind: "boolean" }] },
      },
    },
  ],
};
const U32: FieldSchema = { kind: "integer", min: 0, max: MAX_U32 };
const PARTY_INVITE_ID: FieldSchema = { kind: "integer", min: 1, max: MAX_U32 };
const PARTY_MEMBER: FieldSchema = {
  kind: "object",
  fields: {
    place: { kind: "integer", min: 0, max: PARTY_SIZE - 1 },
    firstName: FIRST_NAME,
    familyName: FAMILY_NAME,
    leader: { kind: "boolean" },
    mayInvite: { kind: "boolean" },
    online: { kind: "boolean" },
    map: { kind: "string", minChars: 0, maxChars: PARTY_MAP_CHARS },
  },
};
/**
 * The most handles `shrines` lists: a map holds at most 64 shrines (ADR-096 decision 14).
 */
export const MAX_SHRINE_HANDLES = 64;

const TILES: FieldSchema = { kind: "sixteenths", max: MAX_MAP_PLACE_TILES };
const MAP_POINT: FieldSchema = { kind: "object", fields: { x: TILES, y: TILES } };
const TRAVEL_NAME: FieldSchema = { kind: "string", minChars: 0, maxChars: MAX_TRAVEL_NAME_CHARS };
const TRAVEL_PLACE: FieldSchema = {
  kind: "tuple",
  items: [
    TRAVEL_NAME,
    TRAVEL_NAME,
    { kind: "enum", values: TRAVEL_PLACE_KINDS },
    TRAVEL_NAME,
    TILES,
    TILES,
    { kind: "integer", min: 0, max: CURRENCY_MAX },
    { kind: "integer", min: 0, max: MAX_TRAVEL_FOCUS },
    { kind: "boolean" },
    { kind: "string", minChars: 0, maxChars: MAX_TRAVEL_REASON_CHARS, pattern: TRAVEL_PLACE_REASON_PATTERN },
  ],
};
const HARVEST_REASON: FieldSchema = { kind: "string", minChars: 1, maxChars: MAX_HARVEST_REASON_CHARS, pattern: HARVEST_REASON_PATTERN };
const TRAVEL_REASON: FieldSchema = { kind: "string", minChars: 1, maxChars: MAX_TRAVEL_REASON_CHARS, pattern: TRAVEL_REASON_PATTERN };
const MAP_PLACE_FIELDS: FieldTable = { handle: U16, x: TILES, y: TILES, name: { kind: "string", minChars: 0, maxChars: MAX_MAP_PLACE_NAME_CHARS } };
const MAP_PLACE_KIND: FieldSchema = { kind: "enum", values: ["waypoint"] };
const MAP_PLACE_ART: FieldSchema = { kind: "object", fields: { appearance: { kind: "integer", min: 0, max: MAX_MAP_ART_APPEARANCE }, x: TILES, y: TILES } };
const MAP_PLACE: FieldSchema = {
  kind: "oneOf",
  of: [
    { kind: "object", fields: MAP_PLACE_FIELDS },
    { kind: "object", fields: { ...MAP_PLACE_FIELDS, art: MAP_PLACE_ART } },
    { kind: "object", fields: { ...MAP_PLACE_FIELDS, kind: MAP_PLACE_KIND } },
    { kind: "object", fields: { ...MAP_PLACE_FIELDS, kind: MAP_PLACE_KIND, art: MAP_PLACE_ART } },
  ],
};

const PERF_COUNTS: FieldSchema = { kind: "object", fields: { characters: U16, creatures: U16, awake: U16, things: U16 } };
const EMOTE_ID: FieldSchema = { kind: "integer", min: 1, max: MAX_EMOTE_ID };

/**
 * A message's field table: every field required, nothing extra.
 */
type FieldTable = Readonly<Record<string, FieldSchema>>;

/**
 * `hello`'s fields; from 6.91.0 a page may add `handoff: true`, its word that it handles a map handoff on its socket
 * (`handoffInitiated`, then a `welcome` naming the map), without which the gateway never moves it to another map.
 */
const HELLO_FIELDS: FieldTable = {
  zoneToken: { kind: "string", minChars: 1, maxChars: MAX_ZONE_TOKEN_CHARS },
  protocol: { kind: "integer", min: PROTOCOL_VERSION, max: PROTOCOL_VERSION },
  content: { kind: "integer", min: 0, max: MAX_U32 },
};

/**
 * `welcome`'s fields before 6.91.0; from it a server adds `map`, the map the character stands on (ADR-106 decision 19),
 * and a welcome without it reads as `main` (`welcomeMap`).
 */
const WELCOME_FIELDS: FieldTable = {
  handle: { kind: "integer", min: 0, max: MAX_U16 },
  worldVersion: { kind: "string", minChars: 1, maxChars: 64 },
  rulesVersion: { kind: "string", minChars: 1, maxChars: 64, pattern: RULES_VERSION_PATTERN },
  spawn: { kind: "object", fields: { x: { kind: "integer", min: 0, max: MAX_U16 }, y: { kind: "integer", min: 0, max: MAX_U16 } } },
  tick: { kind: "integer", min: 0, max: MAX_U32 },
  firstName: FIRST_NAME,
  familyName: FAMILY_NAME,
};

const WELCOME_MAP: FieldSchema = { kind: "string", minChars: 1, maxChars: MAX_HANDOFF_MAP_CHARS };

/**
 * `welcome.crossing` (6.96.0, ADR-147 decisions 16, 18 and 31): the stand position in the new map's frame in sixteenths
 * and the last input sequence the previous map applied, on the `welcome` that follows a crossing.
 */
const WELCOME_CROSSING: FieldSchema = { kind: "object", fields: { x: { kind: "integer", min: 0, max: MAX_U16 }, y: { kind: "integer", min: 0, max: MAX_U16 }, inputSeq: { kind: "integer", min: 0, max: MAX_U32 } } };

/**
 * `welcome.serverTime` (6.100.0, P2.23-T13): the server's wall clock in milliseconds since the Unix epoch when it sent the
 * welcome, from which the page keeps its offset to the server's clock.
 */
const WELCOME_TIME: FieldSchema = { kind: "integer", min: 0, max: Number.MAX_SAFE_INTEGER };

/**
 * Each field table beside the same table with `serverTime` added.
 *
 * @param tables The tables.
 * @returns Every table and its timed twin.
 */
function withServerTime(tables: readonly FieldTable[]): readonly FieldTable[] {
  return tables.flatMap((table) => [table, { ...table, serverTime: WELCOME_TIME }]);
}

const SCHEMAS: Readonly<Record<number, FieldTable>> = {
  [MessageType.Sky]: { sky: { kind: "enum", values: SKY_OVERRIDES } },
  [MessageType.DoorRefused]: { reason: { kind: "enum", values: DOOR_REFUSAL_REASONS } },
  [MessageType.DoorPrompt]: { shown: { kind: "boolean" }, name: { kind: "string", minChars: 0, maxChars: MAX_DOOR_PROMPT_NAME_CHARS } },
  [MessageType.HandoffInitiated]: {
    map: { kind: "string", minChars: 1, maxChars: MAX_HANDOFF_MAP_CHARS },
    name: { kind: "string", minChars: 0, maxChars: MAX_HANDOFF_NAME_CHARS },
    cause: { kind: "enum", values: HANDOFF_CAUSES },
  },
  [MessageType.Ping]: { t: { kind: "integer", min: 0, max: MAX_U32 } },
  [MessageType.Pong]: { t: { kind: "integer", min: 0, max: MAX_U32 } },
  [MessageType.Error]: {
    code: { kind: "integer", min: CloseCode.VersionMismatch, max: CloseCode.QueueFull },
    message: { kind: "string", minChars: 0, maxChars: MAX_ERROR_MESSAGE_CHARS },
  },
  [MessageType.Maintenance]: {
    scheduled: { kind: "boolean" },
    startsInMs: { kind: "integer", min: 0, max: MAX_U32 },
    endsInMs: { kind: "integer", min: NO_MAINTENANCE_END, max: MAX_U32 },
  },
  [MessageType.PerfOffer]: { intervalMs: { kind: "integer", min: MIN_PERF_INTERVAL_MS, max: MAX_PERF_INTERVAL_MS } },
  [MessageType.PerfRequest]: {},
  [MessageType.FeedbackOffer]: {},
  [MessageType.ModerationOffer]: { actions: { kind: "array", of: MODERATION_WORD, maxItems: MAX_MODERATION_WORD_CHARS } },
  [MessageType.Moderation]: { kind: MODERATION_WORD, reason: { kind: "string", minChars: 0, maxChars: MAX_RELAYED_TEXT_UNITS }, endsInMs: { kind: "integer", min: NO_MODERATION_END, max: MAX_U32 } },
  [MessageType.StaffAlert]: { familyName: NAMED_FAMILY, by: FAMILY_NAME, text: RELAYED_TEXT },
  [MessageType.FeedbackAccess]: { state: { kind: "enum", values: FEEDBACK_ACCESS_STATES }, endsInMs: { kind: "integer", min: 0, max: MAX_U32 } },
  [MessageType.Queue]: { place: { kind: "integer", min: 1, max: MAX_U16 }, etaMs: { kind: "integer", min: NO_QUEUE_ESTIMATE, max: MAX_U32 }, length: { kind: "integer", min: 1, max: MAX_U16 } },
  [MessageType.QueueLeave]: {},
  [MessageType.Leave]: { confirm: { kind: "boolean" } },
  [MessageType.StaffBadgeOffer]: { types: { kind: "array", of: { kind: "enum", values: STAFF_TYPES }, minItems: 1, maxItems: STAFF_TYPES.length, distinct: true }, wears: { kind: "enum", values: STAFF_TYPES }, hidden: { kind: "boolean" } },
  [MessageType.StaffNotice]: {
    id: NOTICE_ID,
    kind: { kind: "enum", values: STAFF_NOTICE_KINDS },
    from: { kind: "enum", values: NOTICE_SENDERS },
    badge: { kind: "integer", min: 0, max: BADGE_MAX },
    text: { kind: "string", minChars: 0, maxChars: MAX_RELAYED_TEXT_UNITS },
    agoS: { kind: "integer", min: 0, max: MAX_U32 },
  },
  [MessageType.StaffNoticeAck]: { id: NOTICE_ID },
  [MessageType.StaffNoticeSeen]: { familyName: NAMED_FAMILY, kind: { kind: "enum", values: ACKNOWLEDGED_ACTIONS } },
  [MessageType.LeaveAnswer]: { lock: { kind: "integer", min: 0, max: MAX_U16 }, stay: { kind: "integer", min: 0, max: MAX_U16 } },
  [MessageType.PerfReport]: { tickP50Us: U32, tickP99Us: U32, clients: U16, downP50: U32, downMax: U32, zone: PERF_COUNTS, area: PERF_COUNTS, near: U16, full: U16, quarter: U16 },
  [MessageType.Say]: { text: CHAT_REQUEST_TEXT },
  [MessageType.Whisper]: { firstName: FIRST_NAME, familyName: FAMILY_NAME, text: CHAT_REQUEST_TEXT },
  [MessageType.Block]: { familyName: NAMED_FAMILY },
  [MessageType.Unblock]: { familyName: NAMED_FAMILY },
  [MessageType.Said]: { handle: { kind: "integer", min: 0, max: MAX_U16 }, firstName: FIRST_NAME, familyName: FAMILY_NAME, text: RELAYED_TEXT },
  [MessageType.Whispered]: { sent: { kind: "boolean" }, firstName: FIRST_NAME, familyName: FAMILY_NAME, text: RELAYED_TEXT },
  [MessageType.Blocks]: { families: { kind: "array", of: NAMED_FAMILY, maxItems: MAX_BLOCKED_FAMILIES } },
  [MessageType.ChatRefused]: { reason: { kind: "string", minChars: 1, maxChars: 16, pattern: CHAT_REASON_PATTERN }, waitMs: { kind: "integer", min: 0, max: MAX_U32 } },
  [MessageType.Pose]: { text: CHAT_REQUEST_TEXT },
  [MessageType.Posed]: { handle: { kind: "integer", min: 0, max: MAX_U16 }, firstName: FIRST_NAME, familyName: FAMILY_NAME, text: RELAYED_TEXT },
  [MessageType.NameLookup]: { prefix: LOOKUP_PREFIX },
  [MessageType.NameMatches]: { prefix: LOOKUP_PREFIX, names: { kind: "array", of: MATCHED_NAME, maxItems: MAX_NAME_MATCHES }, waitMs: { kind: "integer", min: 0, max: MAX_U32 } },
  [MessageType.PartyInviteLookup]: { prefix: LOOKUP_PREFIX },
  [MessageType.PartyInviteMatches]: { prefix: LOOKUP_PREFIX, names: { kind: "array", of: MATCHED_NAME, maxItems: MAX_NAME_MATCHES }, waitMs: { kind: "integer", min: 0, max: MAX_U32 } },
  [MessageType.ChannelSay]: { channel: CHANNEL_WORD, text: CHAT_REQUEST_TEXT },
  [MessageType.ChannelJoin]: { channel: CHANNEL_WORD },
  [MessageType.ChannelLeave]: { channel: CHANNEL_WORD },
  [MessageType.ChannelMute]: { familyName: NAMED_FAMILY, channel: CHANNEL_WORD, minutes: MUTE_MINUTES },
  [MessageType.ChannelSaid]: { channel: CHANNEL_WORD, firstName: FIRST_NAME, familyName: FAMILY_NAME, text: RELAYED_TEXT },
  [MessageType.Channels]: { joined: { kind: "array", of: CHANNEL_WORD, maxItems: MAX_LISTED_CHANNELS }, muted: { kind: "array", of: HELD_MUTE, maxItems: MAX_LISTED_CHANNELS } },
  [MessageType.ChannelMuted]: { familyName: NAMED_FAMILY, channel: CHANNEL_WORD, minutes: MUTE_MINUTES },
  [MessageType.Afk]: {},
  [MessageType.Away]: { state: { kind: "integer", min: Away.Active, max: Away.Afk } },
  [MessageType.SpeakerBadge]: { familyName: FAMILY_NAME, badge: { kind: "integer", min: 0, max: BADGE_MAX } },
  [MessageType.WhisperAway]: { firstName: FIRST_NAME, familyName: FAMILY_NAME },
  [MessageType.PartyInvite]: { firstName: FIRST_NAME, familyName: FAMILY_NAME },
  [MessageType.PartyAnswer]: { invite: PARTY_INVITE_ID, accept: { kind: "boolean" } },
  [MessageType.PartyLeave]: {},
  [MessageType.PartyRemove]: { firstName: FIRST_NAME, familyName: FAMILY_NAME },
  [MessageType.PartyPromote]: { firstName: FIRST_NAME, familyName: FAMILY_NAME },
  [MessageType.PartySay]: { text: CHAT_REQUEST_TEXT },
  [MessageType.PartyInvited]: { invite: PARTY_INVITE_ID, firstName: FIRST_NAME, familyName: FAMILY_NAME, members: { kind: "integer", min: 1, max: PARTY_SIZE - 1 }, expiresMs: { kind: "integer", min: 1, max: PARTY_INVITE_MS }, flag: { kind: "integer", min: 0, max: 2 } },
  [MessageType.Party]: { members: { kind: "array", of: PARTY_MEMBER, maxItems: PARTY_SIZE, orderedBy: ["place"] } },
  [MessageType.PartyNotice]: { kind: { kind: "enum", values: PARTY_NOTICE_KINDS }, firstName: FIRST_NAME, familyName: FAMILY_NAME, invite: { kind: "integer", min: 0, max: MAX_U32 } },
  [MessageType.PartySaid]: { firstName: FIRST_NAME, familyName: FAMILY_NAME, text: RELAYED_TEXT },
  [MessageType.PartyRefused]: { reason: { kind: "enum", values: PARTY_REFUSALS }, waitMs: { kind: "integer", min: 0, max: MAX_U32 } },
  [MessageType.PartyGrant]: { firstName: FIRST_NAME, familyName: FAMILY_NAME, mayInvite: { kind: "boolean" } },
  [MessageType.Emotes]: { sets: { kind: "array", of: { kind: "string", minChars: 1, maxChars: MAX_EMOTE_SET_CHARS, pattern: EMOTE_SET_PATTERN }, minItems: 1, maxItems: MAX_EMOTE_SETS } },
  [MessageType.EmojiShown]: { handle: U16, id: EMOTE_ID },
  [MessageType.ActionShown]: { handle: U16, id: EMOTE_ID },
  [MessageType.Interact]: { handle: { kind: "integer", min: 0, max: MAX_U16 }, verb: VERB },
  [MessageType.Interaction]: {
    handle: { kind: "integer", min: 0, max: MAX_U16 },
    verb: VERB,
    ok: { kind: "boolean" },
    text: { kind: "string", minChars: 0, maxChars: MAX_INTERACTION_TEXT_CHARS },
  },
  [MessageType.Dialogue]: {
    handle: NPC_HANDLE,
    dialogue: { kind: "string", minChars: 1, maxChars: 64, pattern: CONTENT_FILE_ID_PATTERN },
    node: DIALOGUE_NODE,
    speaker: U16,
    line: DIALOGUE_LINE_ID,
    text: DIALOGUE_TEXT,
    choices: { kind: "array", of: DIALOGUE_CHOICE, maxItems: MAX_DIALOGUE_CHOICES },
    tier: DIALOGUE_TIER,
    end: { kind: "boolean" },
  },
  [MessageType.DialogueChoose]: { handle: NPC_HANDLE, node: DIALOGUE_NODE, choice: { kind: "integer", min: 0, max: MAX_DIALOGUE_CHOICES - 1 } },
  [MessageType.DialogueClose]: { handle: NPC_HANDLE, reason: { kind: "enum", values: DIALOGUE_CLOSE_REASONS } },
  [MessageType.RespawnOffer]: {
    readyTicks: { kind: "integer", min: 0, max: MAX_U16 },
    points: { kind: "array", of: RESPAWN_POINT, maxItems: MAX_RESPAWN_POINTS, minItems: 1 },
  },
  [MessageType.Bag]: {
    size: { kind: "integer", min: 1, max: MAX_WIRE_BAG_SLOTS },
    items: { kind: "array", of: HELD_SLOT, maxItems: MAX_WIRE_BAG_SLOTS, distinctAt: 0 },
  },
  [MessageType.BagChanged]: {
    slots: { kind: "array", of: { kind: "oneOf", of: [HELD_SLOT, EMPTIED_SLOT] }, minItems: 1, maxItems: MAX_WIRE_BAG_SLOTS },
    cause: { kind: "enum", values: BAG_CHANGE_CAUSES },
  },
  [MessageType.UseItem]: { slot: BAG_SLOT, item: ITEM_ID },
  [MessageType.BagMove]: { from: BAG_SLOT, to: BAG_SLOT },
  [MessageType.BagSplit]: { slot: BAG_SLOT },
  [MessageType.DestroyItem]: { slot: BAG_SLOT, item: ITEM_ID, quantity: QUANTITY },
  [MessageType.DropItem]: { slot: BAG_SLOT, item: ITEM_ID, quantity: QUANTITY },
  [MessageType.BagSort]: { by: { kind: "enum", values: BAG_SORT_ORDERS } },
  [MessageType.BagKeep]: { slot: BAG_SLOT, kept: { kind: "boolean" } },
  [MessageType.BagKept]: { slots: { kind: "array", of: BAG_SLOT, maxItems: MAX_WIRE_BAG_SLOTS, ascending: true } },
  [MessageType.Bar]: { seq: { kind: "integer", min: 0, max: MAX_U16 }, slots: BAR_SLOTS_FIELD },
  [MessageType.BarSet]: { seq: { kind: "integer", min: 1, max: MAX_U16 }, slots: BAR_SLOTS_FIELD },
  [MessageType.ToolBelt]: { seq: { kind: "integer", min: 0, max: MAX_U16 }, slots: TOOL_BELT_SLOTS_FIELD, selected: TOOL_BELT_SELECTED },
  [MessageType.ToolBeltSet]: { seq: { kind: "integer", min: 1, max: MAX_U16 }, slots: TOOL_BELT_SLOTS_FIELD, selected: TOOL_BELT_SELECTED },
  [MessageType.Wallet]: {
    amounts: { kind: "array", of: { kind: "tuple", items: [{ kind: "string", minChars: 1, maxChars: 16, pattern: CURRENCY_CODE_PATTERN }, { kind: "integer", min: 0, max: CURRENCY_MAX }] }, maxItems: MAX_WALLET_CURRENCIES, distinctAt: 0 },
  },
  [MessageType.ItemRefused]: { request: { kind: "enum", values: ITEM_REQUESTS }, slot: BAG_SLOT, reason: { kind: "enum", values: ITEM_REFUSAL_REASONS } },
  [MessageType.Loot]: {
    handle: U16,
    result: { kind: "enum", values: ["open", "unavailable"] },
    lines: { kind: "array", of: { kind: "tuple", items: [CHEST_LINE, ITEM_ID, QUANTITY, { kind: "boolean" }] }, maxItems: MAX_LOOT_ENTRIES },
  },
  [MessageType.PickUp]: { handle: U16 },
  [MessageType.PickUpRefused]: { handle: U16, reason: { kind: "enum", values: PICK_UP_REFUSAL_REASONS }, waitMs: { kind: "integer", min: 0, max: MAX_U32 } },
  [MessageType.LootTake]: { handle: U16, lines: { kind: "array", of: CHEST_LINE, minItems: 1, maxItems: MAX_LOOT_ENTRIES, distinct: true } },
  [MessageType.LootTaken]: {
    handle: U16,
    taken: { kind: "array", of: CHEST_LINE, maxItems: MAX_LOOT_ENTRIES, distinct: true },
    refused: { kind: "array", of: { kind: "tuple", items: [CHEST_LINE, { kind: "enum", values: LOOT_REFUSAL_REASONS }] }, maxItems: MAX_LOOT_ENTRIES },
  },
  [MessageType.LootGained]: {
    items: { kind: "array", of: { kind: "tuple", items: [ITEM_ID, QUANTITY] }, maxItems: MAX_LOOT_ENTRIES },
    lost: { kind: "array", of: { kind: "tuple", items: [ITEM_ID, QUANTITY] }, maxItems: MAX_LOOT_ENTRIES },
    grounded: { kind: "array", of: { kind: "tuple", items: [U16, ITEM_ID, QUANTITY] }, maxItems: MAX_LOOT_ENTRIES },
    coin: { kind: "integer", min: 0, max: CURRENCY_MAX },
    coinRefused: { kind: "boolean" },
  },
  [MessageType.Shrines]: {
    handles: { kind: "array", of: { kind: "integer", min: 0, max: NO_HANDLE - 1 }, maxItems: MAX_SHRINE_HANDLES, ascending: true },
    discovered: { kind: "boolean" },
    found: U16,
    name: { kind: "string", minChars: 0, maxChars: MAX_RESPAWN_POINT_NAME_CHARS },
    xp: U16,
  },
  [MessageType.MapPlaces]: {
    spawn: MAP_POINT,
    places: { kind: "array", of: MAP_PLACE, maxItems: MAX_MAP_PLACES, orderedBy: ["handle", "x", "y"] },
  },
  [MessageType.TravelAsk]: { from: { kind: "enum", values: ["anywhere"] } },
  [MessageType.TravelOffer]: {
    serial: { kind: "integer", min: 0, max: MAX_U32 },
    source: { kind: "enum", values: TRAVEL_OFFER_SOURCES },
    sourceName: TRAVEL_NAME,
    places: { kind: "array", of: TRAVEL_PLACE, maxItems: MAX_TRAVEL_PLACES },
  },
  [MessageType.Travel]: { serial: { kind: "integer", min: 0, max: MAX_U32 }, index: { kind: "integer", min: 0, max: MAX_TRAVEL_PLACES - 1 } },
  [MessageType.TravelCancel]: {},
  [MessageType.Harvest]: { index: { kind: "integer", min: 0, max: MAX_U16 } },
  [MessageType.EdgeNear]: {
    map: { kind: "string", minChars: 1, maxChars: MAX_HANDOFF_MAP_CHARS },
    version: { kind: "string", minChars: 1, maxChars: MAX_EDGE_NEAR_VERSION_CHARS },
    near: { kind: "boolean" },
  },
  [MessageType.CompanionSet]: { out: { kind: "boolean" } },
  [MessageType.Attributes]: {
    values: { kind: "array", of: { kind: "integer", min: 1, max: 999 }, minItems: ATTRIBUTES.length, maxItems: ATTRIBUTES.length },
    effective: { kind: "array", of: { kind: "integer", min: 0, max: 999 }, minItems: ATTRIBUTES.length, maxItems: ATTRIBUTES.length },
    bonus: { kind: "array", of: { kind: "integer", min: -999, max: 999 }, minItems: ATTRIBUTES.length, maxItems: ATTRIBUTES.length },
    points: { kind: "integer", min: 0, max: 999 },
    background: { kind: "oneOf", of: [{ kind: "null" }, { kind: "enum", values: BACKGROUND_IDS }] },
    respec: { kind: "enum", values: ["free", "shrine"] },
  },
  [MessageType.SpendAttributes]: { spends: { kind: "array", of: { kind: "tuple", items: [{ kind: "enum", values: ATTRIBUTES }, { kind: "integer", min: 1, max: MAX_SPEND_POINTS }] }, minItems: 1, maxItems: ATTRIBUTES.length, distinctAt: 0 } },
  [MessageType.RespecAttributes]: {},
  [MessageType.AttributeRefused]: { reason: { kind: "enum", values: ATTRIBUTE_REFUSALS } },
  [MessageType.CompanionName]: { name: { kind: "string", minChars: MIN_FIRST_NAME_CHARS, maxChars: MAX_FIRST_NAME_CHARS, pattern: FIRST_NAME_PATTERN } },
  [MessageType.CompanionSay]: { line: { kind: "string", minChars: 1, maxChars: MAX_COMPANION_LINE_CHARS, pattern: COMPANION_LINE_PATTERN } },
  [MessageType.CompanionNaming]: { state: { kind: "enum", values: COMPANION_NAMING_STATES } },
  [MessageType.CompanionState]: { out: { kind: "boolean" }, name: { kind: "string", minChars: 0, maxChars: MAX_FIRST_NAME_CHARS } },
  [MessageType.DoorBusy]: {},
  [MessageType.Source]: { key: { kind: "string", minChars: 3, maxChars: MAX_SOURCE_KEY_CHARS, pattern: SOURCE_KEY_PATTERN }, lit: { kind: "boolean" } },
  [MessageType.ReadingLayout]: {
    handle: { kind: "integer", min: 0, max: MAX_U16 },
    layout: { kind: "string", minChars: 1, maxChars: MAX_READING_LAYOUT_CHARS, pattern: READING_LAYOUT_PATTERN },
  },
};

/**
 * The messages whose fields depend on one another, each as the field tables it may take: a message passes when it
 * passes any one of them whole. `emote` (ADR-114 decision 6.1) is an action, whose `target` is an entity handle or
 * `NO_HANDLE` for none, or an emoji, whose `target` is always `NO_HANDLE`.
 */
const VARIANTS: Readonly<Record<number, readonly FieldTable[]>> = {
  [MessageType.Hello]: [HELLO_FIELDS, { ...HELLO_FIELDS, handoff: { kind: "boolean" } }],
  [MessageType.Welcome]: withServerTime([WELCOME_FIELDS, { ...WELCOME_FIELDS, map: WELCOME_MAP }, { ...WELCOME_FIELDS, map: WELCOME_MAP, crossing: WELCOME_CROSSING }]),
  [MessageType.TravelState]: [
    { state: { kind: "enum", values: [TRAVEL_STATES[0]] }, name: TRAVEL_NAME, endsTick: { kind: "integer", min: 0, max: MAX_U32 } },
    { state: { kind: "enum", values: [TRAVEL_STATES[1]] }, name: TRAVEL_NAME },
    { state: { kind: "enum", values: [TRAVEL_STATES[2], TRAVEL_STATES[3]] }, name: TRAVEL_NAME, reason: TRAVEL_REASON },
  ],
  [MessageType.HarvestState]: [
    { state: { kind: "enum", values: [HARVEST_STATES[0]] }, index: { kind: "integer", min: 0, max: MAX_U16 }, endsTick: { kind: "integer", min: 0, max: MAX_U32 } },
    { state: { kind: "enum", values: [HARVEST_STATES[1]] }, index: { kind: "integer", min: 0, max: MAX_U16 } },
    { state: { kind: "enum", values: [HARVEST_STATES[2], HARVEST_STATES[3]] }, index: { kind: "integer", min: 0, max: MAX_U16 }, reason: HARVEST_REASON },
  ],
  [MessageType.Emote]: [
    { kind: { kind: "enum", values: ["action"] }, id: EMOTE_ID, target: U16 },
    { kind: { kind: "enum", values: ["emoji"] }, id: EMOTE_ID, target: { kind: "integer", min: NO_HANDLE, max: NO_HANDLE } },
  ],
};

/**
 * Joins the messages of one field table and the messages of several into one lookup by type. A type belongs to one
 * list or the other: this runs as the module loads, so a type placed in both, or given an empty list of tables, stops
 * the build's first test rather than being read from whichever list came last.
 *
 * @param single Each message's one table, by type.
 * @param several Each message's alternative tables, by type.
 * @returns Every type's tables, a list of one for a message of `single`.
 * @throws Error naming the type when it is in both lists, or when its list of alternatives is empty.
 * @example
 * joinFieldTables({ 1: "a" }, { 2: ["b", "c"] }).get(2);
 * // ["b", "c"]
 */
export function joinFieldTables<Table>(single: Readonly<Record<number, Table>>, several: Readonly<Record<number, readonly Table[]>>): ReadonlyMap<number, readonly Table[]> {
  const joined = new Map<number, readonly Table[]>();
  for (const [type, table] of Object.entries(single)) {
    joined.set(Number(type), [table]);
  }
  for (const [type, tables] of Object.entries(several)) {
    if (joined.has(Number(type))) {
      throw new Error(`message type ${type} has one field table and several`);
    }
    if (tables.length === 0) {
      throw new Error(`message type ${type} has no field table`);
    }
    joined.set(Number(type), tables);
  }
  return joined;
}

/**
 * Every text message's field tables by type: the one table of `SCHEMAS`, or the several of `VARIANTS`.
 */
const TABLES: ReadonlyMap<number, readonly FieldTable[]> = joinFieldTables(SCHEMAS, VARIANTS);

/**
 * A parsed text message: its type and the validated fields.
 */
export type TextMessage = { readonly type: MessageType } & Readonly<Record<string, unknown>>;

/**
 * Outcome of parsing a text frame: the message; a well-formed frame of the current version whose type the
 * catalogue does not know, which a page ignores and a server closes on; a client message the catalogue declares and
 * this build does not yet handle, which a gateway drops on its not-ready window (ADR-074 decision 1); or the close code
 * of a fault.
 */
export type TextVerdict = { readonly kind: "ok"; readonly message: TextMessage } | { readonly kind: "unknown"; readonly type: number } | { readonly kind: "notReady"; readonly type: number } | { readonly kind: "close"; readonly code: CloseCode };

const MALFORMED: TextVerdict = { kind: "close", code: CloseCode.Malformed };
const VERSION_MISMATCH: TextVerdict = { kind: "close", code: CloseCode.VersionMismatch };

/**
 * Whether a value is a plain JSON object without prototype tricks.
 *
 * @param value Candidate.
 * @returns True for a plain object.
 */
function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value) && Object.getPrototypeOf(value) === Object.prototype && !Object.hasOwn(value, "__proto__");
}

/**
 * Whether no two items of a list of lists hold the same value at one position.
 *
 * @param list The list, each item already held to its schema.
 * @param position The position compared.
 * @returns True when every item's value there is its own.
 */
function distinctAt(list: readonly unknown[], position: number): boolean {
  const seen = new Set<unknown>();
  for (const item of list) {
    const key: unknown = Array.isArray(item) ? item[position] : undefined;
    if (seen.has(key)) {
      return false;
    }
    seen.add(key);
  }
  return true;
}

/**
 * Whether each item of a list is greater than the one before it, which also means none repeats.
 *
 * @param list The list, each item already held to its schema as a number.
 * @returns True for an empty list, a list of one and a strictly ascending list.
 */
function ascending(list: readonly unknown[]): boolean {
  for (let index = 1; index < list.length; index += 1) {
    if (!((list[index] as number) > (list[index - 1] as number))) {
      return false;
    }
  }
  return true;
}

/**
 * Whether each object of a list comes after the one before it, compared by the named fields in turn, each a number,
 * which also means none repeats.
 *
 * @param list The list, each item already held to its schema as an object holding the fields.
 * @param fields The fields compared, the first deciding unless equal.
 * @returns True for an empty list, a list of one and a strictly ordered list.
 */
function orderedBy(list: readonly unknown[], fields: readonly string[]): boolean {
  for (let index = 1; index < list.length; index += 1) {
    const before = list[index - 1] as Record<string, number>;
    const item = list[index] as Record<string, number>;
    const field = fields.find((name) => item[name] !== before[name]);
    if (field === undefined || (item[field] as number) < (before[field] as number)) {
      return false;
    }
  }
  return true;
}

/**
 * Validates a value against a field schema.
 *
 * @param value The value.
 * @param schema The schema.
 * @returns True when valid.
 */
function validField(value: unknown, schema: FieldSchema): boolean {
  switch (schema.kind) {
    case "integer":
      return typeof value === "number" && Number.isInteger(value) && value >= schema.min && value <= schema.max;
    case "sixteenths":
      return typeof value === "number" && Number.isInteger(value * SIXTEENTHS) && value >= 0 && value <= schema.max;
    case "string":
      return typeof value === "string" && value.length >= schema.minChars && value.length <= schema.maxChars && (schema.pattern === undefined || schema.pattern.test(value));
    case "enum":
      return typeof value === "string" && schema.values.includes(value);
    case "boolean":
      return typeof value === "boolean";
    case "null":
      return value === null;
    case "object":
      return isPlainObject(value) && validFields(value, schema.fields);
    case "array":
      return Array.isArray(value) && value.length <= schema.maxItems && value.length >= (schema.minItems ?? 0) && value.every((item) => validField(item, schema.of)) && (schema.distinct !== true || new Set(value.map((item) => JSON.stringify(item))).size === value.length) && (schema.distinctAt === undefined || distinctAt(value, schema.distinctAt)) && (schema.ascending !== true || ascending(value)) && (schema.orderedBy === undefined || orderedBy(value, schema.orderedBy));
    case "tuple":
      return Array.isArray(value) && value.length === schema.items.length && schema.items.every((item, index) => validField(value[index], item));
    case "oneOf":
      return schema.of.some((option) => validField(value, option));
  }
}

/**
 * Validates an object against a field table: every field present and valid, nothing extra.
 *
 * @param record The object.
 * @param fields The table.
 * @returns True when valid.
 */
function validFields(record: Record<string, unknown>, fields: FieldTable): boolean {
  const names = Object.keys(fields);
  const keys = Object.keys(record);
  if (keys.length !== names.length) {
    return false;
  }
  return names.every((name) => Object.hasOwn(record, name) && validField(record[name], fields[name] as FieldSchema));
}

/**
 * Parses and validates a text frame that arrived from a direction. The transport must have checked the
 * byte length against `MAX_TEXT_FRAME_BYTES` already. A frame whose `v` is the current version and whose
 * `type` is a whole number the catalogue does not hold, outside the debug range on a production gateway, is
 * `unknown`; a client message the catalogue declares as text and this build does not yet handle is `notReady`, its
 * fields unread (ADR-074 decision 1); a type the catalogue holds for the other direction or another frame kind is
 * malformed.
 *
 * @param text The frame.
 * @param direction Who sent it.
 * @param production True on a production gateway.
 * @returns The message, `unknown` or `notReady` with its type, or the close code.
 */
export function parseTextFrame(text: string, direction: MessageDirection, production: boolean): TextVerdict {
  let value: unknown;
  try {
    value = JSON.parse(text);
  } catch {
    return MALFORMED;
  }
  if (!isPlainObject(value)) {
    return MALFORMED;
  }
  const { v, type, ...rest } = value;
  if (v !== PROTOCOL_VERSION) {
    return typeof v === "number" ? VERSION_MISMATCH : MALFORMED;
  }
  if (typeof type !== "number" || !Number.isInteger(type)) {
    return MALFORMED;
  }
  if (messageInfo(type) === undefined && !(production && type >= DEBUG_RANGE_START)) {
    return { kind: "unknown", type };
  }
  if (isNotReady(type, direction, "text") && !(production && type >= DEBUG_RANGE_START)) {
    return { kind: "notReady", type };
  }
  if (!isAllowed(type, direction, "text", production)) {
    return MALFORMED;
  }
  const tables = TABLES.get(type);
  if (tables === undefined || !tables.some((table) => validFields(rest, table))) {
    if (type === MessageType.Hello && isPlainObject(rest) && typeof rest["protocol"] === "number" && rest["protocol"] !== PROTOCOL_VERSION) {
      return VERSION_MISMATCH;
    }
    return MALFORMED;
  }
  return { kind: "ok", message: { type: type as MessageType, ...rest } };
}

/**
 * What a message's fields break in a field table, for the error `encodeText` throws.
 *
 * @param record The fields.
 * @param table The table.
 * @returns The first of the table's fields that is missing or out of range, named in words; "fields outside its
 *   schema" when every field passes and others are carried; null when the fields pass the table.
 */
function tableFault(record: Record<string, unknown>, table: FieldTable): string | null {
  for (const [name, field] of Object.entries(table)) {
    if (!validField(record[name], field)) {
      return `field ${name} is missing or out of range`;
    }
  }
  return Object.keys(record).length === Object.keys(table).length ? null : "fields outside its schema";
}

/**
 * Serialises a message with the protocol version, after checking it against its schema.
 *
 * @param message Type and fields.
 * @returns The frame text.
 * @throws Error naming the message type and the field when one is missing or out of range, or saying that fields
 *   are carried outside the schema; for a message of several field tables that passes none, naming each table in
 *   order with what the message breaks in it.
 */
export function encodeText(message: TextMessage): string {
  const { type, ...rest } = message;
  const tables = TABLES.get(type);
  if (tables === undefined) {
    throw new Error(`message type ${type} has no text schema`);
  }
  const faults = tables.map((table) => tableFault(rest, table));
  if (!faults.includes(null)) {
    if (faults.length === 1) {
      const fault = faults[0] as string;
      throw new Error(fault.startsWith("field ") ? `message ${type} ${fault}` : `message ${type} carries ${fault}`);
    }
    throw new Error(`message ${type} passes none of its ${faults.length} field tables: ${faults.map((fault, index) => `table ${index + 1}, ${fault}`).join("; ")}`);
  }
  return JSON.stringify({ v: PROTOCOL_VERSION, type, ...rest });
}
